import { Calendar } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import { formatCurrency } from '@/utils';
import type { Deal } from '@/types';

interface SlippedDealsPanelProps {
  deals: Deal[];
  onDealClick?: (deals: Deal[]) => void;
}

export function SlippedDealsPanel({ deals, onDealClick }: SlippedDealsPanelProps) {
  const now = new Date();
  const totalValue = deals.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500/20">
            <Calendar className="h-4 w-4 text-orange-400" />
          </div>
          <div>
            <Tooltip {...KPI_TOOLTIPS.slippedDeals}>
              <h3 className="text-lg font-semibold text-white">Deals Past Close Date</h3>
            </Tooltip>
            <p className="text-xs text-zinc-500">{deals.length} deals need attention</p>
          </div>
        </div>
        <button
          onClick={() => onDealClick?.(deals)}
          className="text-xs text-orange-400 hover:text-orange-300 font-medium"
        >
          View All →
        </button>
      </div>

      <div className="space-y-2">
        {deals.slice(0, 3).map(deal => {
          const closeDate = new Date(deal.closeDate!);
          const daysOverdue = Math.floor((now.getTime() - closeDate.getTime()) / (1000 * 60 * 60 * 24));
          return (
            <div key={deal.id} className="flex items-center justify-between rounded-lg bg-zinc-800/50 p-3">
              <div>
                <p className="text-sm font-medium text-white">{deal.name}</p>
                <p className="text-xs text-zinc-500">{deal.company} • {deal.stage}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-orange-400">{formatCurrency(deal.value)}</p>
                <p className="text-xs text-red-400">{daysOverdue}d overdue</p>
              </div>
            </div>
          );
        })}
        {deals.length > 3 && (
          <p className="text-xs text-zinc-500 text-center pt-2">
            +{deals.length - 3} more deals ({formatCurrency(totalValue)} total)
          </p>
        )}
      </div>
    </div>
  );
}
